import { createSlice } from '@reduxjs/toolkit'

/**
 * Sales requests — what the field sent, waiting for the office.
 *
 * ⚠️ NOT `src/drafts`. That module is browser-local autosave and has nothing to
 * do with this. See Config.js for why the word "request" is used throughout the
 * UI while the API path keeps the server's "drafts".
 */
export const SalesRequestSlice = createSlice({
    name: 'salesRequest',
    initialState: {
        list: [],
        pageInfo: null,
        // The request currently open on the conversion screen, together with
        // everything the admin has to see BEFORE converting: whether a customer
        // is about to be created, and whether the stock is still there.
        preview: null,
        // Per-line money the admin fills in. Keyed by draft_item_id so a line
        // cannot be matched by position — an admin who removes a line and comes
        // back would otherwise silently reprice a different product.
        lineValues: {},
        orderValues: {
            so_no: '',
            date: '',
            discount: '',
            carrying_charge: '',
            labour_charge: '',
            vat: '',
            paid: '',
            // Empty, never 'Cash'. The method has to be CHOSEN from the
            // server's list, because that list carries `settles_instantly` —
            // and a cheque booked as cash inflates the bank balance the moment
            // it is handed over.
            payment_method: '',
            note: '',
            customer_type_id: '',
            // The admin's pick when one phone number matches several customers.
            customer_id: '',
        },
    },

    reducers: {
        setSalesRequestList: (state, action) => {
            state.list = action.payload
        },
        setSalesRequestPageInfo: (state, action) => {
            state.pageInfo = action.payload
        },
        setSalesRequestPreview: (state, action) => {
            state.preview = action.payload
        },
        resetSalesRequestPreview: (state) => {
            state.preview = null
            state.lineValues = {}
            state.orderValues = {
                so_no: '',
                date: '',
                discount: '',
                carrying_charge: '',
                labour_charge: '',
                vat: '',
                paid: '',
                payment_method: '',
                note: '',
                customer_type_id: '',
                customer_id: '',
            }
        },
        setSalesRequestLineValue: (state, action) => {
            const { id, name, value } = action.payload
            state.lineValues[id] = { ...(state.lineValues[id] || {}), [name]: value }
        },
        setSalesRequestOrderValue: (state, action) => {
            state.orderValues[action.payload.name] = action.payload.value
        },
    },
})

export const {
    setSalesRequestList,
    setSalesRequestPageInfo,
    setSalesRequestPreview,
    resetSalesRequestPreview,
    setSalesRequestLineValue,
    setSalesRequestOrderValue,
} = SalesRequestSlice.actions

export default SalesRequestSlice.reducer
